import { observable, syncState } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';

// Default AI model for new conversations
const DEFAULT_AI_MODEL = 'gemini-3-flash-preview';

// AI Conversations store with Supabase sync (local-first)
// Private to user - filter by user_id
export const aiConversations$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_ai_conversations',
  // Filter to only current user's conversations
  filter: (query) => {
    const userId = auth$.user?.id?.get?.() || auth$.user?.get?.()?.id;
    if (userId) {
      return query.eq('user_id', userId);
    }
    return query;
  },
  // Custom create/update with error handling (upsert fallback)
  create: createSyncCreate('reedle_ai_conversations'),
  update: createSyncUpdate('reedle_ai_conversations'),
  // Use default sync config
  ...defaultSyncConfig,
  // Enable realtime for multi-device sync
  realtime: true,
  // Persist data locally
  persist: {
    name: 'reedle-ai-conversations',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

// Get conversation by article ID
export const getConversation = (articleId) => {
  const userId = auth$.user?.id?.get?.() || auth$.user?.get?.()?.id;
  if (!userId || !articleId) return null;
  
  const conversations = aiConversations$.get();
  if (!conversations || typeof conversations !== 'object') return null;
  
  return Object.values(conversations).find(
    c => c && !c.deleted && c.article_id === articleId && c.user_id === userId
  ) || null;
};

// Get or create conversation for an article
export const getOrCreateConversation = (articleId) => {
  const userId = auth$.user?.id?.get?.() || auth$.user?.get?.()?.id;
  if (!userId || !articleId) return null;

  let conversation = getConversation(articleId);

  if (!conversation) {
    const id = uuidv4();
    const now = new Date().toISOString();
    conversation = {
      id,
      article_id: articleId,
      user_id: userId,
      messages: [],
      preferred_model: DEFAULT_AI_MODEL,
      quote_sources: {}, // Store quote source metadata for "add as comment" feature
      created_at: now,
      updated_at: now,
    };
    aiConversations$[id].set(conversation);
  }

  return conversation;
};

// Update quote sources for a conversation
export const updateQuoteSources = (articleId, quoteSources) => {
  const conversation = getConversation(articleId);
  if (!conversation) return null;

  aiConversations$[conversation.id].quote_sources.set(quoteSources);
  aiConversations$[conversation.id].updated_at.set(new Date().toISOString());

  return conversation.id;
};

// Get quote sources for a conversation
export const getQuoteSources = (articleId) => {
  const conversation = getConversation(articleId);
  return conversation?.quote_sources || {};
};

// Add message to conversation
export const addMessage = (articleId, message) => {
  const conversation = getOrCreateConversation(articleId);
  if (!conversation) return null;
  
  const messages = [...(conversation.messages || []), message];
  aiConversations$[conversation.id].messages.set(messages);
  aiConversations$[conversation.id].updated_at.set(new Date().toISOString());
  
  return conversation.id;
};

// Update messages in conversation
export const updateMessages = (articleId, messages) => {
  const conversation = getConversation(articleId);
  if (!conversation) return null;
  
  aiConversations$[conversation.id].messages.set(messages);
  aiConversations$[conversation.id].updated_at.set(new Date().toISOString());
  
  return conversation.id;
};

// Update preferred model
export const updatePreferredModel = (articleId, model) => {
  const conversation = getConversation(articleId);
  if (!conversation) return null;
  
  aiConversations$[conversation.id].preferred_model.set(model);
  aiConversations$[conversation.id].updated_at.set(new Date().toISOString());
  
  return conversation.id;
};

// Clear conversation (delete all messages)
export const clearConversation = async (articleId) => {
  const conversation = getConversation(articleId);
  if (!conversation) return false;

  // Clear messages and update timestamp
  aiConversations$[conversation.id].messages.set([]);
  aiConversations$[conversation.id].updated_at.set(new Date().toISOString());

  // CRITICAL: Clear localStorage persistence to prevent rehydration
  // Without this, Legend-State's retrySync will recover old data from localStorage
  try {
    const state$ = syncState(aiConversations$);
    if (typeof state$?.clearPersist === 'function') {
      await state$.clearPersist();
      console.log('[aiConversations$] Cleared persistence after clearing conversation');
    }
  } catch (e) {
    console.warn('[aiConversations$] Failed to clear persistence:', e);
  }

  return true;
};

// Delete conversation entirely
export const deleteConversation = (articleId) => {
  const conversation = getConversation(articleId);
  if (!conversation) return false;
  
  aiConversations$[conversation.id].deleted.set(true);
  aiConversations$[conversation.id].updated_at.set(new Date().toISOString());
  
  return true;
};

